import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';
import { ShellService } from '../shell/shell.service';
import { DashboardLayoutPreset, dashboardLayoutPreset } from '../shell/dashboard-layout-presets.component';
import { SharedService } from '../shared/services/shared.service';
import { ClientService } from '../modules/client/services/client.service';
import { CommonService } from '../modules/reports/services/common/common.service';
import { DashboardNotification, InspectorDashboardService } from '../inspector-dashboard/inspector-dashboard.service';
import { OnlineSchedulingConfirmComponent } from '../shared/components/online-scheduling/online-scheduling-confirm/online-scheduling-confirm.component';

type WidgetId = 'notifications' | 'incomplete-inspections' | 'wants-earlier' | 'need-payment-proofed'
    | 'ready-to-send' | 'pending-acceptance' | 'agent-outreach';
type LoadState = 'loading' | 'ready' | 'error';

// Mockup administrator/ order.
const ALL_WIDGETS: WidgetId[] = ['notifications', 'incomplete-inspections', 'wants-earlier', 'need-payment-proofed',
    'ready-to-send', 'pending-acceptance', 'agent-outreach'];
// Shown only while environment.inspectionReports is on, as on the classic dashboard.
const REPORT_WIDGETS: WidgetId[] = ['incomplete-inspections', 'need-payment-proofed', 'ready-to-send', 'pending-acceptance',
    'agent-outreach'];
// Branch-filtered queues (api/report/wants_earlier, api/_report/inspections/*, api/agent-outreach/list).
const QUEUES: WidgetId[] = ['incomplete-inspections', 'wants-earlier', 'need-payment-proofed', 'ready-to-send', 'agent-outreach'];

const OUTREACH_PAGE = 200;

/**
 * Administrator / staff dashboard (Inspectmite redesign, mockup administrator/). The work queues of
 * the classic dashboard (DashboardComponent, still at /dashboard-classic with its reports) as
 * draggable, collapsible widgets: Notifications, Inspections: Incomplete, Wants Earlier, Need
 * Payment / Proofed, Ready to Send, Online Schedule Pending Acceptance and Agent Outreach. The
 * classic dashboard is no longer linked (route kept).
 *
 * Access is unchanged: every signed-in user who is not an inspector gets these queues, as before.
 * Widget order, collapsed widgets and the layout preset are per-user preferences (ShellService).
 */
@Component({
    selector: 'app-admin-dashboard',
    templateUrl: './admin-dashboard.component.html',
    styleUrls: ['../inspector-dashboard/inspector-dashboard.component.scss', './admin-dashboard.component.scss']
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
    readonly titles: { [id: string]: string } = {
        'notifications': 'Notifications',
        'incomplete-inspections': 'Inspections: Incomplete',
        'wants-earlier': 'Wants Earlier',
        'need-payment-proofed': 'Inspections: Need Payment / Proofed',
        'ready-to-send': 'Inspections: Ready to Send',
        'pending-acceptance': 'Online Schedule Pending Acceptance',
        'agent-outreach': 'Agent Outreach'
    };

    widgets: WidgetId[] = [];
    layout: WidgetId[] = [];
    collapsed: string[] = [];
    preset: DashboardLayoutPreset = 'balanced';
    dragging: WidgetId = null;
    dragOver: WidgetId = null;

    notifications: DashboardNotification[] = [];
    branches: any[] = [];
    // Per queue: the branch picked in its toolbar ('' = All), its rows and load state.
    branch: { [id: string]: string } = {};
    rows: { [id: string]: any[] } = {};
    state: { [id: string]: LoadState } = {};

    // Agent Outreach pages through the list (200 at a time) like the classic widget.
    outreachTotal = 0;
    outreachHasMore = false;
    outreachLoadingMore = false;
    private outreachOffset = 0;
    private inspectors: any[] = [];
    private subscriptions: Subscription[] = [];

    constructor(
        private shell: ShellService,
        private shared: SharedService,
        private common: CommonService,
        private inspectorApi: InspectorDashboardService,
        private modal: NgbModal,
        private clients: ClientService
    ) { }

    ngOnInit() {
        // Notifications are not shown until the feature exists (mockup data-feature-deferred).
        const available = ALL_WIDGETS.filter(id => id !== 'notifications');
        this.widgets = environment.inspectionReports ? available
            : available.filter(id => REPORT_WIDGETS.indexOf(id) === -1);
        this.subscriptions.push(this.shell.preferences$.subscribe(() => this.readPreferences()));
        this.subscriptions.push(this.inspectorApi.notifications().subscribe(items => this.notifications = items));
        this.shared.getBranch().subscribe(data => this.branches = data || []);
        QUEUES.filter(id => this.has(id)).forEach(id => {
            this.branch[id] = '';
            this.load(id);
        });
        if (this.has('pending-acceptance')) {
            this.loadPending();
            this.common.getInspectors().subscribe(data => this.inspectors = data || []);
        }
        // Deferred a tick: the top bar has already been checked in this change-detection pass.
        Promise.resolve().then(() => this.shell.setPageHeader({ greeting: true }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.shell.setPageHeader(null);
    }

    has(id: WidgetId): boolean {
        return this.widgets.indexOf(id) !== -1;
    }

    // ---- Layout ----

    setPreset(preset: DashboardLayoutPreset) {
        this.preset = preset;
        this.shell.set('admin_dashboard_preset', preset);
    }

    isCollapsed(id: WidgetId): boolean {
        return this.collapsed.indexOf(id) !== -1;
    }

    toggleCollapsed(id: WidgetId) {
        this.collapsed = this.isCollapsed(id) ? this.collapsed.filter(c => c !== id) : this.collapsed.concat(id);
        this.shell.set('admin_dashboard_collapsed', this.collapsed);
    }

    onDragStart(event: DragEvent, id: WidgetId) {
        this.dragging = id;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', id);   // Firefox needs data to start a drag
    }

    onDragOver(event: DragEvent, id: WidgetId) {
        if (this.dragging && this.dragging !== id) {
            event.preventDefault();
            this.dragOver = id;
        }
    }

    onDrop(event: DragEvent, id: WidgetId) {
        event.preventDefault();
        if (this.dragging && this.dragging !== id) {
            this.swap(this.dragging, id);
        }
        this.onDragEnd();
    }

    onDragEnd() {
        this.dragging = null;
        this.dragOver = null;
    }

    // Keyboard alternative to dragging: arrow keys on the handle move the widget one place.
    onHandleKey(event: KeyboardEvent, id: WidgetId) {
        const steps: { [key: string]: number } = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 };
        const step = steps[event.key];
        if (!step) {
            return;
        }
        event.preventDefault();
        const target = this.layout[this.layout.indexOf(id) + step];
        if (target) {
            this.swap(id, target);
        }
    }

    private swap(a: WidgetId, b: WidgetId) {
        const layout = this.layout.slice();
        const i = layout.indexOf(a);
        const j = layout.indexOf(b);
        layout[i] = b;
        layout[j] = a;
        this.layout = layout;
        this.shell.set('admin_dashboard_layout', layout);
    }

    private readPreferences() {
        const saved = this.shell.get('admin_dashboard_layout', null);
        let layout = this.widgets.slice();
        if (Array.isArray(saved)) {
            // Keep only widgets this user has, then append any the saved layout doesn't have yet.
            const known = saved.filter((id: WidgetId) => this.has(id));
            layout = known.concat(this.widgets.filter(id => known.indexOf(id) === -1));
        }
        // Only when it changed, so an unrelated preference (e.g. dark mode) doesn't rebuild the widgets.
        if (layout.join() !== this.layout.join()) {
            this.layout = layout;
        }
        const collapsed = this.shell.get('admin_dashboard_collapsed', []);
        this.collapsed = Array.isArray(collapsed) ? collapsed : [];
        this.preset = dashboardLayoutPreset(this.shell.get('admin_dashboard_preset', 'balanced'), 'balanced');
    }

    // ---- Queues ----

    apply(id: WidgetId) {
        this.load(id);
    }

    clear(id: WidgetId) {
        this.branch[id] = '';
        if (id === 'agent-outreach') {
            this.outreachStatus = 'pending';
            this.outreachCategory = '';
        }
        this.load(id);
    }

    private load(id: WidgetId) {
        this.state[id] = 'loading';
        const filter = { branch: this.branch[id] || '' };
        if (id === 'agent-outreach') {
            this.outreachOffset = 0;
            this.loadOutreach(Object.assign(filter, { status: this.outreachStatus }));
            return;
        }
        const request = {
            'incomplete-inspections': () => this.shared.inspectionsIncomplete(filter),
            'wants-earlier': () => this.shared.wantsEarlier(filter),
            'need-payment-proofed': () => this.shared.inspectionsNeedPayment(filter),
            'ready-to-send': () => this.shared.inspectionsReadySend(filter)
        }[id];
        request().subscribe(
            data => {
                this.rows[id] = Array.isArray(data) ? data : [];
                this.state[id] = 'ready';
            },
            () => this.state[id] = 'error'
        );
    }

    count(id: WidgetId): string {
        const n = id === 'agent-outreach' ? this.outreachTotal : (this.rows[id] || []).length;
        return n + (n === 1 ? ' record' : ' records');
    }

    // Notes flagged as alerts on the job (the classic widgets' Notes column).
    alertNotes(job): string[] {
        return (job.notes || []).filter(n => n.is_note_alert == '1').map(n => n.job_note_note);
    }

    name(person): string {
        return person ? [person.client_first_name || person.user_first_name, person.client_last_name || person.user_last_name]
            .filter(v => v).join(' ') : '';
    }

    joined(...parts: string[]): string {
        return parts.filter(v => v).join(' · ');
    }

    // ---- Online Schedule Pending Acceptance ----

    loadPending() {
        this.state['pending-acceptance'] = 'loading';
        this.shared.getOnlineBookings().subscribe(
            data => {
                this.rows['pending-acceptance'] = Array.isArray(data) ? data : [];
                this.state['pending-acceptance'] = 'ready';
            },
            () => this.state['pending-acceptance'] = 'error'
        );
    }

    viewBooking(booking) {
        const modalRef = this.modal.open(OnlineSchedulingConfirmComponent, { size: 'lg' });
        modalRef.componentInstance.inspectionData = booking;
        modalRef.componentInstance.inspectors = this.inspectors;
        modalRef.result.then(() => this.loadPending(), () => this.loadPending());
    }

    discardBooking(booking) {
        Swal({
            title: 'Discard this request?',
            text: 'The online scheduling request is removed from the list.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Discard'
        }).then(result => {
            if (result.value) {
                this.shared.discardOnlineBookingJob({ id: booking.id }).subscribe(() => this.loadPending(), () => this.loadPending());
            }
        });
    }

    // ---- Agent Outreach ----

    private loadOutreach(filter, append = false) {
        this.shared.agentOutreachList(Object.assign({}, filter, { offset: this.outreachOffset, limit: OUTREACH_PAGE })).subscribe(
            res => {
                const data = (res && res.data) || [];
                this.rows['agent-outreach'] = append ? (this.rows['agent-outreach'] || []).concat(data) : data;
                this.outreachTotal = res ? res.total : 0;
                this.outreachHasMore = !!(res && res.has_more);
                this.outreachLoadingMore = false;
                this.state['agent-outreach'] = 'ready';
            },
            () => {
                this.outreachLoadingMore = false;
                this.state['agent-outreach'] = 'error';
            }
        );
    }

    loadMoreOutreach() {
        this.outreachLoadingMore = true;
        this.outreachOffset += OUTREACH_PAGE;
        this.loadOutreach({ branch: this.branch['agent-outreach'] || '', status: this.outreachStatus }, true);
    }

    // Mockup count buttons (All Tasks / New Agents / Re-engagement / VIP), by our trigger types.
    readonly outreachCategories = [
        { key: '', label: 'All Tasks' },
        { key: 'new_agent', label: 'New Agents' },
        { key: 're_engagement', label: 'Re-engagement' },
        { key: 'agent_dropoff', label: 'Agent Dropoff' },
        { key: 'vip_earned', label: 'VIP' },
    ];
    outreachCategory = '';
    // Mockup 2026-10-22: results stay collapsed until a category box is clicked; clicking the
    // open box again closes them (and the open tasks).
    outreachExpanded = false;
    // Mockup "Tasks" select: Pending (the to-do list), Completed (last 30 days) or All.
    outreachStatus: 'pending' | 'completed' | 'all' = 'pending';

    outreachCount(key: string): number {
        const rows = this.rows['agent-outreach'] || [];
        return key ? rows.filter(row => (row.trigger_types || []).indexOf(key) >= 0).length : rows.length;
    }

    toggleOutreachCategory(key: string) {
        this.outreachExpanded = this.outreachCategory !== key || !this.outreachExpanded;
        this.outreachCategory = key;
    }

    // Hidden results show nothing, so Mark Visible Complete has nothing to act on.
    get outreachShown(): any[] {
        if (!this.outreachExpanded) {
            return [];
        }
        const rows = this.rows['agent-outreach'] || [];
        const key = this.outreachCategory;
        return key ? rows.filter(row => (row.trigger_types || []).indexOf(key) >= 0) : rows;
    }

    /** First open of a task: the client's profile and order history (api/client/details, once). */
    onOutreachToggle(item, open: boolean) {
        if (!open || !item.client_id || item._profile || item._profileLoading) {
            return;
        }
        item._profileLoading = true;
        item._profileError = '';
        this.clients.getClientDetails(item.client_id).subscribe(
            res => {
                const d = (res && res.data) || {};
                const client = d.client || {};
                const created = client.client_date_created;
                item._profile = {
                    since: created && String(created).indexOf('0000-00-00') !== 0 ? String(created).replace(' ', 'T') : '',
                    mostRecent: d.most_recent_date ? String(d.most_recent_date).replace(' ', 'T') : '',
                    last12Properties: d.last_12_properties || 0,
                    last12Completions: d.last_12_completions || 0,
                    lifetimeProperties: d.lifetime_properties || 0,
                    lifetimeCompletions: d.lifetime_completions || 0,
                    perProperty: d.dollar_per_inspection || 0,
                    balance: d.associated_balance || 0,
                    jobs: Array.isArray(d.jobs) ? d.jobs : [],
                    notes: (client.client_notes || '').trim(),
                    inspectors: (Array.isArray(d.preferred_inspectors) ? d.preferred_inspectors : [])
                        .map(u => [u.user_first_name, u.user_last_name].filter(Boolean).join(' ')),
                };
                item._profileLoading = false;
            },
            () => {
                item._profileLoading = false;
                item._profileError = 'The client profile could not be loaded.';
            }
        );
    }

    initialsOf(name: string): string {
        return (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('');
    }

    triggerLabel(type: string): string {
        return ({ new_agent: 'New', agent_dropoff: 'Agent Dropoff', re_engagement: 'Re-Engagement', vip_earned: 'VIP Earned' } as any)[type]
            || type;
    }

    hasActivity(item): boolean {
        const d = item.last_activity_date;
        return !!d && d !== '0000-00-00 00:00:00' && d !== '0000-00-00';
    }

    // Same rule as the classic widget: tasks whose trigger date is older than N days.
    // Pending tasks among the ones shown (category filter applied), for Mark Visible Complete.
    get visiblePending(): any[] {
        return this.outreachShown.filter(row => row.status !== 'completed');
    }

    // Mockup "Mark Visible Complete": every pending task currently listed.
    completeVisibleOutreach() {
        const rows = this.visiblePending;
        if (!rows.length) {
            return;
        }
        Swal({
            title: 'Mark Visible Complete',
            text: 'Mark the ' + rows.length + ' pending outreach ' + (rows.length === 1 ? 'task' : 'tasks') + ' shown as complete?',
            showCancelButton: true,
            confirmButtonText: 'Mark complete'
        }).then(result => {
            if (result.value) {
                this.shared.agentOutreachCompleteVisible({ client_ids: rows.map(row => row.client_id) }).subscribe(data => {
                    if (data && data.success) {
                        this.load('agent-outreach');
                    }
                });
            }
        });
    }

    completeOutreach(item) {
        this.shared.agentOutreachComplete({ client_id: item.client_id }).subscribe(data => {
            if (data && data.success) {
                if (this.outreachStatus === 'pending') {
                    this.rows['agent-outreach'] = this.rows['agent-outreach'].filter(row => row.client_id !== item.client_id);
                    this.outreachTotal--;
                } else {
                    item.status = 'completed';
                }
            }
        });
    }

    saveOutreachNote(item) {
        this.shared.agentOutreachSaveNote({ client_id: item.client_id, notes: item.notes }).subscribe(() => {
            item._saved = true;
            setTimeout(() => item._saved = false, 2000);
        });
    }

    trackById(index: number, id: string) {
        return id;
    }
}
